import { useId, type InputHTMLAttributes, type ReactNode } from 'react';
import './FormControls.css';

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  /** Rendered alongside the label in the same row (e.g. a "Forgot your Password?" affordance) — kept as
   * a generic slot so this component never needs to know what that content is. */
  labelExtra?: ReactNode;
  errorMessage?: string | null | undefined;
}

/** Generic labeled text input with inline validation-error display. Justified as a shared primitive
 * because it is already known to be reused by later Identity auth screens (password recovery, MFA), not
 * built as a speculative design-system rewrite. */
export function TextField({ label, labelExtra, errorMessage, ...inputProps }: TextFieldProps) {
  const id = useId();
  const errorId = errorMessage ? `${id}-error` : undefined;

  return (
    <div className="form-field">
      <div className="form-field-label-row">
        <label htmlFor={id}>{label}</label>
        {labelExtra}
      </div>
      <input
        id={id}
        aria-invalid={errorMessage ? true : undefined}
        aria-describedby={errorId}
        {...inputProps}
      />
      {errorMessage && (
        <p id={errorId} className="form-field-error" role="alert">
          {errorMessage}
        </p>
      )}
    </div>
  );
}
